import React, { useState } from "react";
import { Modal, Text, LegacyStack, RadioButton, Box, Divider, Link } from "@shopify/polaris";

interface NotifyProps {
  isOpen: boolean;
  onCancelUpdate: () => void;
  onConfirmUpdate: (notify: boolean) => Promise<void>;
  onBack: () => void;
}

export default function ({ isOpen, onCancelUpdate, onConfirmUpdate, onBack }: NotifyProps): React.JSX.Element {
  const [notify, setNotify] = useState(true);

  const EmailPreview = () => {
    return (
      <Box
        background="bg-surface-tertiary"
        borderColor="border-secondary"
        borderWidth="1"
        borderStyle="solid"
        padding="4"
        borderRadius="4"
      >
        <LegacyStack spacing="baseTight" vertical>
          <Text variant="bodySm" as="p" color="subdued" fontWeight="semibold">
            EMAIL PREVIEW
          </Text>

          <Text variant="bodyMd" as="p">
            <Text as="span" fontWeight="semibold">
              Subject:&nbsp;
            </Text>
            <Text as="span" color="subdued">
              Your subscription price is changing
            </Text>
          </Text>

          <Divider />

          <Text variant="bodyMd" as="p" color="subdued">
            Hi {"{customer_name}"},
          </Text>

          <Text variant="bodyMd" as="p" color="subdued">
            We&apos;re writing to let you know that the price of one or more products in your subscription{" "}
            {"{subscription_program_name}"} is changing.
          </Text>

          <Text variant="bodyMd" as="p" color="subdued">
            These updated prices will apply starting with your next scheduled charge.
          </Text>

          <Text variant="bodyMd" as="p" fontWeight="semibold">
            Products with price changes:
          </Text>

          <Text variant="bodyMd" as="p" color="subdued">
            <ul>
              <li>
                {"{product_name}"}: {"{old_price}"} → {"{new_price}"}. [Where applicable] Your discounted price:{" "}
                {"{discounted_price}"} with {"{discount}"} off.
              </li>
            </ul>
          </Text>

          <Text variant="bodyMd" as="p" color="subdued">
            You can manage your subscription at any time — skip, swap, or cancel — from your account portal.
          </Text>

          <Text variant="bodyMd" as="p">
            <Link>Manage my subscription →</Link>
          </Text>
        </LegacyStack>
      </Box>
    );
  };

  return (
    <Modal
      open={isOpen}
      onClose={onCancelUpdate}
      title="Notify customers"
      primaryAction={{
        content: notify ? "Update and send notification" : "Update without notification",
        onAction: () => onConfirmUpdate(notify),
      }}
      secondaryActions={[
        {
          content: "Back",
          onAction: onBack,
        },
      ]}
    >
      <Modal.Section>
        <LegacyStack spacing="loose" vertical>
          <Text variant="bodyMd" as="p">
            Would you like to send an email to affected subscribers about this price change?
          </Text>
          <LegacyStack vertical spacing="tight">
            <Box
              background={notify ? "bg-interactive-subdued" : "bg-surface"}
              borderColor={notify ? "border-focus" : "border-secondary"}
              borderWidth={notify ? "2" : "1"}
              borderStyle="solid"
              padding="4"
              borderRadius="4"
            >
              <RadioButton
                label={
                  <Text as="span" fontWeight="semibold">
                    Yes, notify customers
                  </Text>
                }
                helpText="Send an automated email with the product affected, old vs new price, effective timing (next charge), and a link to manage their subscription."
                checked={notify}
                onChange={() => setNotify(true)}
              />
            </Box>

            <Box
              background={notify ? "bg-surface" : "bg-interactive-subdued"}
              borderColor={notify ? "border-secondary" : "border-focus"}
              borderWidth={notify ? "1" : "2"}
              borderStyle="solid"
              padding="4"
              borderRadius="4"
            >
              <RadioButton
                label={
                  <Text as="span" fontWeight="semibold">
                    No, skip notification
                  </Text>
                }
                helpText="No communication will be sent. Prices update silently on the next billing cycle."
                checked={!notify}
                onChange={() => setNotify(false)}
              />
            </Box>

            {!!notify && <EmailPreview />}
          </LegacyStack>
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
}
